/**
 * CAT Profile Teaser Page
 *
 * Shown to public users who haven't activated the beta feature.
 * Design adapted from Google Stitch MCP generated HTML.
 */

import { UnifiedHeader } from '../components/UnifiedHeader';
import { UnifiedFooter } from '../components/UnifiedFooter';
import { SEOHead } from '../components/SEOHead';
import { getCanonicalUrl } from '../utils/siteConfig';
import { useNavigate } from 'react-router-dom';

export default function CATProfileTeaserPage() {
  const navigate = useNavigate();

  const handleLoginClick = () => {
    // Set redirect so after login, user comes back to /ho-so-nang-luc
    localStorage.setItem('redirectAfterLogin', '/ho-so-nang-luc');
    navigate('/login');
  };

  return (
    <div className='min-h-screen bg-background-light dark:bg-background-dark flex flex-col'>
      <SEOHead
        title='Hồ sơ năng lực CAT | Hướng nghiệp CAT'
        description='Khám phá Hồ sơ năng lực CAT - Công cụ đánh giá và định hướng nghề nghiệp cho học sinh THPT.'
        canonicalUrl={getCanonicalUrl('/ho-so-nang-luc')}
        keywords='hồ sơ năng lực, CAT Profile, đánh giá năng lực, hướng nghiệp THPT, định hướng nghề nghiệp'
      />
      <UnifiedHeader />

      <main className='flex-1 pt-20 pb-12 lg:pt-24'>
        {/* ===== HERO SECTION ===== */}
        <section className='relative overflow-hidden px-4 sm:px-6 lg:px-8 py-20 lg:py-32'>
          <div className='absolute inset-0 -z-10 bg-gradient-to-br from-[#D4841C] via-[#E8A84C] to-[#F5C97A] opacity-10' />
          <div className='absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#D4841C]/20 via-transparent to-transparent' />
          {/* Floating geometric shapes */}
          <div
            className='absolute top-20 right-[10%] h-20 w-20 rounded-full border-4 border-[#D4841C]/10 animate-bounce'
            style={{ animationDuration: '6s' }}
          />
          <div className='absolute bottom-40 left-[5%] h-12 w-12 rotate-45 border border-[#D4841C]/20 bg-[#D4841C]/5 animate-pulse' />
          <div className='absolute top-[40%] right-[25%] h-4 w-4 rounded-full bg-[#E8A84C]/30 blur-sm animate-pulse' />
          <div
            className='absolute bottom-[20%] right-[10%] h-32 w-32 rounded-full border border-dashed border-[#D4841C]/20 opacity-30'
            style={{ animation: 'spin 12s linear infinite' }}
          />

          <div className='mx-auto max-w-7xl text-center relative z-10'>
            {/* Badge */}
            <div className='inline-flex items-center gap-2 rounded-full border border-yellow-400/30 bg-yellow-500/10 px-4 py-1.5 mb-8 shadow-md shadow-yellow-500/10 backdrop-blur-sm'>
              <span className='material-symbols-outlined text-yellow-500 text-sm animate-pulse'>
                star
              </span>
              <span className='text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-yellow-600 to-yellow-400 bg-clip-text text-transparent'>
                Miễn phí
              </span>
              <span className='material-symbols-outlined text-yellow-500 text-sm animate-pulse'>
                star
              </span>
            </div>

            <h1 className='mx-auto max-w-5xl text-5xl font-black leading-tight tracking-tight text-slate-900 dark:text-white lg:text-7xl'>
              Hồ Sơ Năng Lực{' '}
              <span className='text-transparent bg-clip-text bg-gradient-to-r from-[#D4841C] via-[#E8A84C] to-[#F5C97A]'>
                CAT
              </span>
            </h1>
            <p className='mx-auto mt-6 max-w-2xl text-lg font-medium text-slate-600 dark:text-slate-300 lg:text-xl'>
              Biết rõ năng lực - Chọn đúng ngành học. Nền tảng dữ liệu giáo dục giúp học sinh định
              vị bản thân và chinh phục ước mơ.
            </p>

            <div className='mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row'>
              <button
                onClick={handleLoginClick}
                className='relative overflow-hidden group flex h-14 min-w-[220px] items-center justify-center rounded-2xl bg-gradient-to-r from-[#D4841C] to-[#E8A84C] px-8 text-lg font-bold text-white shadow-xl shadow-[#D4841C]/30 transition-all hover:translate-y-[-2px] hover:shadow-2xl'
              >
                <span className='relative z-10 flex items-center gap-2'>
                  Đăng nhập để bắt đầu{' '}
                  <span className='material-symbols-outlined group-hover:translate-x-1 transition-transform'>
                    arrow_forward
                  </span>
                </span>
                <div className='absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300' />
              </button>
            </div>
            <p className='mt-4 text-xs font-medium text-slate-500'>
              Đăng nhập để khám phá hồ sơ năng lực của bạn. Hoàn toàn miễn phí!
            </p>
          </div>
        </section>

        {/* ===== FEATURE CARDS ===== */}
        <section className='px-4 sm:px-6 lg:px-8 pb-24 pt-10 -mt-10'>
          <div className='mx-auto max-w-7xl'>
            <div className='grid gap-8 md:grid-cols-3'>
              {/* Card 1: Score Analysis */}
              <div className='group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-1 shadow-lg shadow-slate-200/50 transition-all hover:shadow-xl hover:translate-y-[-4px] dark:border-slate-700 dark:bg-slate-800 dark:shadow-none'>
                <div className='rounded-2xl bg-slate-50 p-6 h-full flex flex-col dark:bg-slate-900/50'>
                  <div className='mb-4 flex items-center justify-between'>
                    <div className='flex items-center gap-2'>
                      <div className='h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'>
                        <span className='material-symbols-outlined text-lg'>analytics</span>
                      </div>
                      <h3 className='font-bold text-slate-900 dark:text-white'>Phân tích điểm</h3>
                    </div>
                    <span className='text-xs font-medium text-slate-500 bg-white px-2 py-1 rounded-md border border-slate-100 dark:bg-slate-800 dark:border-slate-700'>
                      Mới nhất
                    </span>
                  </div>
                  <div className='space-y-3 mb-4 mt-2'>
                    {[
                      { name: 'Toán', score: 8.5, pct: 85, color: 'bg-blue-500' },
                      { name: 'Ngữ Văn', score: 7.2, pct: 72, color: 'bg-purple-500' },
                      { name: 'Tiếng Anh', score: 6.8, pct: 68, color: 'bg-red-500' },
                    ].map(s => (
                      <div key={s.name}>
                        <div className='flex justify-between text-xs mb-1'>
                          <span className='font-semibold text-slate-700 dark:text-slate-300'>
                            {s.name}
                          </span>
                          <span className='font-bold text-slate-900 dark:text-white'>
                            {s.score}
                          </span>
                        </div>
                        <div className='h-2 w-full bg-slate-100 rounded-full dark:bg-slate-800'>
                          <div
                            className={`h-2 ${s.color} rounded-full`}
                            style={{ width: `${s.pct}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className='mt-auto h-16 w-full flex items-end justify-between gap-1 px-1'>
                    <div className='w-full bg-blue-500/20 h-[40%] rounded-t-sm' />
                    <div className='w-full bg-blue-500/40 h-[60%] rounded-t-sm' />
                    <div className='w-full bg-blue-500/60 h-[50%] rounded-t-sm' />
                    <div className='w-full bg-blue-500/80 h-[80%] rounded-t-sm relative group-hover:scale-105 transition-transform origin-bottom'>
                      <div className='absolute -top-6 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[9px] px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity'>
                        Cao nhất
                      </div>
                    </div>
                    <div className='w-full bg-blue-500 h-[75%] rounded-t-sm' />
                  </div>
                </div>
              </div>

              {/* Card 2: Holland Chart */}
              <div className='group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-1 shadow-lg shadow-slate-200/50 transition-all hover:shadow-xl hover:translate-y-[-4px] dark:border-slate-700 dark:bg-slate-800 dark:shadow-none'>
                <div className='rounded-2xl bg-slate-50 p-6 h-full flex flex-col dark:bg-slate-900/50'>
                  <div className='mb-4 flex items-center justify-between'>
                    <div className='flex items-center gap-2'>
                      <div className='h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center text-green-600 dark:bg-green-900/30 dark:text-green-400'>
                        <span className='material-symbols-outlined text-lg'>psychology</span>
                      </div>
                      <h3 className='font-bold text-slate-900 dark:text-white'>Gợi ý ngành</h3>
                    </div>
                  </div>
                  <div className='flex-1 flex items-center justify-center relative py-4'>
                    {/* Grid background */}
                    <div className='absolute inset-0 flex items-center justify-center opacity-10'>
                      <svg
                        className='w-40 h-40 text-slate-900 dark:text-white'
                        viewBox='0 0 100 100'
                      >
                        <polygon
                          fill='none'
                          points='50,5 90,25 90,75 50,95 10,75 10,25'
                          stroke='currentColor'
                          strokeWidth='1'
                        />
                        <polygon
                          fill='none'
                          points='50,25 75,37.5 75,62.5 50,75 25,62.5 25,37.5'
                          stroke='currentColor'
                          strokeWidth='1'
                        />
                        <line
                          stroke='currentColor'
                          strokeWidth='0.5'
                          x1='50'
                          x2='50'
                          y1='50'
                          y2='5'
                        />
                        <line
                          stroke='currentColor'
                          strokeWidth='0.5'
                          x1='50'
                          x2='90'
                          y1='50'
                          y2='25'
                        />
                        <line
                          stroke='currentColor'
                          strokeWidth='0.5'
                          x1='50'
                          x2='90'
                          y1='50'
                          y2='75'
                        />
                        <line
                          stroke='currentColor'
                          strokeWidth='0.5'
                          x1='50'
                          x2='50'
                          y1='50'
                          y2='95'
                        />
                        <line
                          stroke='currentColor'
                          strokeWidth='0.5'
                          x1='50'
                          x2='10'
                          y1='50'
                          y2='75'
                        />
                        <line
                          stroke='currentColor'
                          strokeWidth='0.5'
                          x1='50'
                          x2='10'
                          y1='50'
                          y2='25'
                        />
                      </svg>
                    </div>
                    {/* Data chart */}
                    <svg
                      className='w-40 h-40 drop-shadow-md transform transition-transform group-hover:scale-110 duration-500'
                      viewBox='0 0 100 100'
                    >
                      <polygon
                        fill='rgba(16, 185, 129, 0.2)'
                        points='50,15 80,35 70,65 50,85 20,60 30,30'
                        stroke='#10B981'
                        strokeWidth='2'
                      />
                      <circle cx='50' cy='15' fill='#10B981' r='2' />
                      <circle cx='80' cy='35' fill='#10B981' r='2' />
                      <circle cx='70' cy='65' fill='#10B981' r='2' />
                    </svg>
                    <div className='absolute top-2 left-1/2 -translate-x-1/2 text-[10px] font-bold bg-white dark:bg-slate-800 px-1.5 py-0.5 rounded shadow-sm text-green-600'>
                      Nghiên cứu
                    </div>
                    <div className='absolute bottom-2 right-10 text-[10px] font-bold bg-white dark:bg-slate-800 px-1.5 py-0.5 rounded shadow-sm text-blue-600'>
                      Xã hội
                    </div>
                    <div className='absolute bottom-10 left-4 text-[10px] font-bold bg-white dark:bg-slate-800 px-1.5 py-0.5 rounded shadow-sm text-purple-600'>
                      Nghệ thuật
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: PDF Export */}
              <div className='group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-1 shadow-lg shadow-slate-200/50 transition-all hover:shadow-xl hover:translate-y-[-4px] dark:border-slate-700 dark:bg-slate-800 dark:shadow-none'>
                <div className='rounded-2xl bg-slate-50 p-6 h-full flex flex-col dark:bg-slate-900/50'>
                  <div className='mb-4 flex items-center justify-between'>
                    <div className='flex items-center gap-2'>
                      <div className='h-8 w-8 rounded-lg bg-red-100 flex items-center justify-center text-red-600 dark:bg-red-900/30 dark:text-red-400'>
                        <span className='material-symbols-outlined text-lg'>picture_as_pdf</span>
                      </div>
                      <h3 className='font-bold text-slate-900 dark:text-white'>Xuất hồ sơ</h3>
                    </div>
                    <span className='material-symbols-outlined text-slate-400 group-hover:text-[#D4841C] transition-colors'>
                      download
                    </span>
                  </div>
                  <div className='relative flex-1 bg-white border border-slate-200 shadow-sm rounded-lg p-3 mx-2 transform rotate-2 transition-transform group-hover:rotate-0 duration-300 dark:bg-slate-800 dark:border-slate-700'>
                    <div className='absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center text-white text-[8px] font-bold shadow-sm'>
                      PDF
                    </div>
                    <div className='flex gap-2 mb-2 border-b border-slate-100 pb-2 dark:border-slate-700'>
                      <div className='h-8 w-8 rounded-full bg-slate-200 shrink-0' />
                      <div className='flex flex-col gap-1 w-full justify-center'>
                        <div className='h-2 w-3/4 bg-slate-800 rounded-full opacity-80 dark:bg-slate-400' />
                        <div className='h-1.5 w-1/2 bg-slate-300 rounded-full dark:bg-slate-600' />
                      </div>
                    </div>
                    <div className='space-y-1.5'>
                      <div className='h-1.5 w-full bg-slate-100 rounded-full dark:bg-slate-700' />
                      <div className='h-1.5 w-full bg-slate-100 rounded-full dark:bg-slate-700' />
                      <div className='h-1.5 w-2/3 bg-slate-100 rounded-full dark:bg-slate-700' />
                    </div>
                    <div className='mt-3 flex gap-1'>
                      <div className='h-8 flex-1 bg-slate-50 border border-slate-100 rounded dark:bg-slate-700/50 dark:border-slate-600' />
                      <div className='h-8 flex-1 bg-slate-50 border border-slate-100 rounded dark:bg-slate-700/50 dark:border-slate-600' />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== DASHBOARD PREVIEW ===== */}
        <section className='px-4 sm:px-6 lg:px-8 py-10 relative'>
          <div className='mx-auto max-w-6xl'>
            <div className='text-center mb-10'>
              <h2 className='text-3xl font-bold tracking-tight text-slate-900 dark:text-white'>
                Xem trước giao diện
              </h2>
              <p className='mt-2 text-slate-600 dark:text-slate-400'>
                Dữ liệu trực quan, dễ dàng theo dõi
              </p>
            </div>

            {/* Laptop frame */}
            <div className='relative rounded-[2rem] bg-slate-900 p-3 shadow-2xl ring-1 ring-slate-900/10 lg:p-4'>
              <div className='relative overflow-hidden rounded-xl bg-white dark:bg-slate-950 flex flex-col min-h-[500px]'>
                {/* Browser dots */}
                <div className='flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3 dark:bg-slate-900 dark:border-slate-800'>
                  <div className='flex gap-1.5'>
                    <div className='h-3 w-3 rounded-full bg-red-400' />
                    <div className='h-3 w-3 rounded-full bg-yellow-400' />
                    <div className='h-3 w-3 rounded-full bg-green-400' />
                  </div>
                  <div className='mx-auto h-6 w-1/3 rounded-md bg-white shadow-sm border border-slate-200 dark:bg-slate-800 dark:border-slate-700' />
                </div>

                {/* Dashboard content */}
                <div className='flex-1 p-6 lg:p-8 bg-slate-50 dark:bg-slate-900/50 grid grid-cols-1 lg:grid-cols-3 gap-6'>
                  <div className='lg:col-span-1 space-y-6'>
                    {/* Score overview */}
                    <div className='bg-white p-5 rounded-xl shadow-sm border border-slate-100 dark:bg-slate-800 dark:border-slate-700'>
                      <h4 className='text-sm font-semibold text-slate-500 mb-2 uppercase tracking-wide'>
                        Tổng quan
                      </h4>
                      <div className='flex items-end gap-2'>
                        <span className='text-4xl font-bold text-slate-900 dark:text-white'>
                          7.5
                        </span>
                        <span className='text-sm font-medium text-green-500 mb-1 bg-green-50 px-2 py-0.5 rounded-full dark:bg-green-900/30'>
                          Điểm TB
                        </span>
                      </div>
                      <div className='mt-4 text-xs text-slate-400'>
                        Cập nhật lần cuối: 2 giờ trước
                      </div>
                    </div>
                    {/* Subject groups */}
                    <div className='bg-white p-5 rounded-xl shadow-sm border border-slate-100 dark:bg-slate-800 dark:border-slate-700'>
                      <h4 className='text-sm font-semibold text-slate-500 mb-4 uppercase tracking-wide'>
                        Nhóm môn học
                      </h4>
                      <div className='space-y-3'>
                        <div>
                          <div className='flex items-center justify-between'>
                            <span className='text-sm font-medium text-slate-700 dark:text-slate-300'>
                              Tự nhiên
                            </span>
                            <span className='text-sm font-bold text-blue-600'>8.2</span>
                          </div>
                          <div className='h-1.5 w-full bg-slate-100 rounded-full overflow-hidden dark:bg-slate-700 mt-1'>
                            <div className='h-full bg-blue-500 w-[82%]' />
                          </div>
                        </div>
                        <div>
                          <div className='flex items-center justify-between mt-2'>
                            <span className='text-sm font-medium text-slate-700 dark:text-slate-300'>
                              Xã hội
                            </span>
                            <span className='text-sm font-bold text-orange-500'>6.8</span>
                          </div>
                          <div className='h-1.5 w-full bg-slate-100 rounded-full overflow-hidden dark:bg-slate-700 mt-1'>
                            <div className='h-full bg-orange-500 w-[68%]' />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Major recommendation table */}
                  <div className='lg:col-span-2'>
                    <div className='bg-white p-6 rounded-xl shadow-sm border border-slate-100 h-full dark:bg-slate-800 dark:border-slate-700'>
                      <h4 className='text-lg font-bold text-slate-900 mb-6 dark:text-white'>
                        Đề xuất ngành học
                      </h4>
                      <div className='overflow-x-auto'>
                        <table className='w-full text-left text-sm'>
                          <thead>
                            <tr className='border-b border-slate-100 dark:border-slate-700 text-slate-500'>
                              <th className='pb-3 font-semibold'>Tên ngành</th>
                              <th className='pb-3 font-semibold'>Khối xét tuyển</th>
                              <th className='pb-3 font-semibold'>Dự báo</th>
                              <th className='pb-3 font-semibold text-right'>Trạng thái</th>
                            </tr>
                          </thead>
                          <tbody className='divide-y divide-slate-100 dark:divide-slate-700'>
                            <tr className='group'>
                              <td className='py-4 font-medium text-slate-900 dark:text-slate-200'>
                                Kỹ thuật phần mềm
                              </td>
                              <td className='py-4 text-slate-500'>A00, A01</td>
                              <td className='py-4 text-slate-500'>90% phù hợp</td>
                              <td className='py-4 text-right'>
                                <span className='inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-1 text-xs font-semibold text-green-700 ring-1 ring-inset ring-green-600/20 dark:bg-green-900/30 dark:text-green-400'>
                                  <span className='h-1.5 w-1.5 rounded-full bg-green-600' /> ĐẠT
                                </span>
                              </td>
                            </tr>
                            <tr className='group'>
                              <td className='py-4 font-medium text-slate-900 dark:text-slate-200'>
                                Quản trị kinh doanh
                              </td>
                              <td className='py-4 text-slate-500'>A01, D01</td>
                              <td className='py-4 text-slate-500'>85% phù hợp</td>
                              <td className='py-4 text-right'>
                                <span className='inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-1 text-xs font-semibold text-green-700 ring-1 ring-inset ring-green-600/20 dark:bg-green-900/30 dark:text-green-400'>
                                  <span className='h-1.5 w-1.5 rounded-full bg-green-600' /> ĐẠT
                                </span>
                              </td>
                            </tr>
                            <tr className='group opacity-70'>
                              <td className='py-4 font-medium text-slate-900 dark:text-slate-200'>
                                Marketing
                              </td>
                              <td className='py-4 text-slate-500'>D01, C00</td>
                              <td className='py-4 text-slate-500'>Cần cải thiện</td>
                              <td className='py-4 text-right'>
                                <span className='inline-flex items-center gap-1 rounded-full bg-yellow-50 px-2 py-1 text-xs font-semibold text-yellow-700 ring-1 ring-inset ring-yellow-600/20 dark:bg-yellow-900/30 dark:text-yellow-400'>
                                  Thiếu 0.5
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Glass overlay */}
                <div className='absolute inset-0 top-[60%] bg-gradient-to-b from-transparent via-white/40 to-white/90 backdrop-blur-[2px] flex items-end justify-center pb-20 dark:via-slate-900/40 dark:to-slate-900/90 z-10'>
                  <div className='flex flex-col items-center'>
                    <span className='material-symbols-outlined text-4xl text-slate-400 mb-2 animate-bounce'>
                      lock_open
                    </span>
                    <span className='text-sm font-bold text-slate-500 uppercase tracking-widest bg-white/50 px-4 py-1 rounded-full backdrop-blur-md dark:bg-slate-800/50 dark:text-slate-300 shadow-sm border border-white/20'>
                      Mở khóa để xem đầy đủ
                    </span>
                  </div>
                </div>
              </div>
              {/* Laptop notch */}
              <div className='mx-auto h-4 w-[12%] rounded-b-xl bg-slate-800 opacity-50' />
            </div>
          </div>
        </section>

        {/* ===== BOTTOM CTA ===== */}
        <section className='px-4 sm:px-6 lg:px-8 py-24'>
          <div className='mx-auto max-w-4xl overflow-hidden rounded-[2.5rem] bg-slate-900 dark:bg-slate-950 p-12 text-center text-white relative shadow-2xl shadow-[#D4841C]/20'>
            <div className='absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#D4841C]/30 via-slate-900 to-slate-900' />
            <div className='relative z-10 flex flex-col items-center'>
              <h2 className='text-3xl font-black lg:text-5xl mb-6'>Sẵn sàng khám phá bản thân?</h2>
              <p className='mb-10 text-lg text-slate-300 max-w-lg mx-auto'>
                Đăng nhập và bắt đầu hành trình khám phá năng lực, định hướng nghề nghiệp của bạn.
              </p>
              <button
                onClick={handleLoginClick}
                className='group relative flex h-16 items-center justify-center rounded-2xl bg-white px-10 text-xl font-bold text-slate-900 transition-all hover:scale-105 hover:bg-slate-50 hover:shadow-[0_0_40px_-10px_rgba(255,255,255,0.3)]'
              >
                <span className='mr-2'>Đăng nhập ngay</span>
                <span className='material-symbols-outlined transition-transform group-hover:translate-x-1'>
                  arrow_forward
                </span>
              </button>
              <p className='mt-6 text-sm text-slate-400 font-medium'>
                <span className='inline-block h-2 w-2 rounded-full bg-green-500 mr-2 animate-pulse' />
                Hoàn toàn miễn phí
              </p>
            </div>
          </div>
        </section>
      </main>

      <UnifiedFooter />
    </div>
  );
}
